/**
 * A storage wrapper that provides automatic fallback capabilities for web storage.
 *
 * This class attempts to use the best available storage mechanism in the following priority:
 * 1. localStorage (persistent across sessions)
 * 2. sessionStorage (temporary, cleared when tab closes)
 * 3. Cookies (fallback when storage is disabled/unavailable)
 *
 * Use cases:
 * - Browsers with storage disabled
 * - Private/incognito mode with storage restrictions
 * - Environments where storage quota is exceeded
 * - Cookie-only environments
 *
 * @example
 * ```typescript
 * const storage = new LocalStorageWithFallback();
 * storage.set('user', { name: 'John' });
 * const user = storage.get<{ name: string }>('user');
 * storage.remove('user');
 * ```
 */
export class LocalStorageWithFallback {
  /** The resolved storage mechanism, or null if only cookies are available */
  private storage: Storage | null;

  /**
   * Creates a new LocalStorageWithFallback instance.
   * Automatically detects and selects the best available storage mechanism.
   */
  constructor() {
    this.storage = this.resolveStorage();
  }

  /**
   * Detects and returns the best available storage mechanism.
   * Tests localStorage first, then sessionStorage, with proper quota/permission checks.
   *
   * @returns Storage instance (localStorage or sessionStorage), or null if neither is available
   * @private
   */
  private resolveStorage(): Storage | null {
    // Server-side rendering check
    if (typeof window === 'undefined') {
      return null;
    }

    // Try localStorage first (persists across sessions)
    try {
      const test = '__test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return localStorage;
    } catch {}

    // Fall back to sessionStorage (cleared when tab closes)
    try {
      const test = '__test__';
      sessionStorage.setItem(test, test);
      sessionStorage.removeItem(test);
      return sessionStorage;
    } catch {}

    // Neither storage is available, will use cookies
    return null;
  }

  /**
   * Checks whether persistent storage is available.
   *
   * @returns true if localStorage or sessionStorage is available, false if only cookies can be used
   */
  isPersistent(): boolean {
    return this.storage !== null;
  }

  /**
   * Retrieves a value from storage by key.
   * First attempts to read from web storage (localStorage/sessionStorage),
   * then falls back to cookies if storage is unavailable.
   *
   * @template T - The expected type of the stored value
   * @param key - The storage key to retrieve
   * @returns The parsed value of type T, or null if:
   *   - The key doesn't exist
   *   - The value cannot be parsed as JSON
   *   - An error occurs during retrieval
   */
  get<T>(key: string): T | null {
    // Try web storage first
    if (this.storage) {
      try {
        const raw = this.storage.getItem(key);
        if (raw) {
          return JSON.parse(raw) as T;
        }
      } catch {}
    }

    // SSR: no storage available
    if (typeof window === 'undefined') {
      return null;
    }

    // Fall back to cookies
    const cookieValue = this.getCookie(key);
    if (!cookieValue) {
      return null;
    }

    try {
      return JSON.parse(cookieValue) as T;
    } catch {
      return null;
    }
  }

  /**
   * Stores a value in storage with an optional time-to-live.
   * First attempts to write to web storage (localStorage/sessionStorage),
   * then falls back to cookies if storage is unavailable or quota is exceeded.
   *
   * @template T - The type of value to store
   * @param key - The storage key
   * @param value - The value to store (will be JSON serialized)
   * @param ttlSeconds - Time-to-live in seconds (default: 300). Only applies to cookie fallback;
   *                     web storage items persist until explicitly removed or cleared
   */
  set<T>(key: string, value: T, ttlSeconds = 300): void {
    let serialized: string;
    try {
      serialized = JSON.stringify(value);
    } catch {
      return;
    }

    // Try web storage first
    if (this.storage) {
      try {
        this.storage.setItem(key, serialized);
        return;
      } catch {}
    }

    // SSR: no storage available
    if (typeof window === 'undefined') {
      return;
    }

    // Fall back to cookies
    this.setCookie(key, serialized, ttlSeconds);
  }

  /**
   * Removes a value from both web storage and cookies.
   * Ensures the key is deleted from all possible storage locations.
   *
   * @param key - The storage key to remove
   */
  remove(key: string): void {
    // Remove from web storage if available
    if (this.storage) {
      try {
        this.storage.removeItem(key);
      } catch {}
    }

    // SSR: no storage available
    if (typeof window === 'undefined') {
      return;
    }

    // Also remove from cookies to ensure complete cleanup
    this.deleteCookie(key);
  }

  /**
   * Sets a cookie with the specified name, value, and expiration.
   * Automatically applies appropriate security settings based on protocol.
   *
   * @param name - The cookie name
   * @param value - The cookie value (will be URL encoded)
   * @param ttlSeconds - Time-to-live in seconds
   * @private
   */
  private setCookie(name: string, value: string, ttlSeconds: number) {
    const expires = new Date(Date.now() + ttlSeconds * 1000).toUTCString();

    // Only set Secure flag on HTTPS to avoid cookie rejection on HTTP
    const secure = window.location.protocol === 'https:' ? 'Secure;' : '';

    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(
      value,
    )}; Expires=${expires}; Path=/; ${secure} SameSite=Lax`;
  }

  /**
   * Retrieves a cookie value by name.
   *
   * @param name - The cookie name to retrieve
   * @returns The decoded cookie value, or null if not found
   * @private
   */
  private getCookie(name: string): string | null {
    const escapedName = encodeURIComponent(name).replace(
      /[.*+?^${}()|[\]\\]/g,
      '\\$&',
    );
    const match = document.cookie.match(
      new RegExp(`(^| )${escapedName}=([^;]+)`),
    );
    return match ? decodeURIComponent(match[2]) : null;
  }

  /**
   * Deletes a cookie by setting its expiration to a past date.
   *
   * @param name - The cookie name to delete
   * @private
   */
  private deleteCookie(name: string) {
    const secure = window.location.protocol === 'https:' ? 'Secure;' : '';
    document.cookie = `${encodeURIComponent(
      name,
    )}=; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=/; ${secure} SameSite=Lax`;
  }
}
